import { Check, Image as ImageIcon, Loader2 } from 'lucide-react'
import { cn } from '~/lib/utils'
import type { AutoCapturePayload } from '../types'

interface AutoCaptureFieldProps {
  payload: AutoCapturePayload | null
  isCapturingScreenshot: boolean
  screenshotError: string | null
}

/**
 * Renders the auto-captured context as a checked-off list. Each line uses a
 * visible Check to communicate "captured — no action needed".
 */
export function AutoCaptureField({
  payload,
  isCapturingScreenshot,
  screenshotError,
}: AutoCaptureFieldProps) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        Auto-captured (no action needed)
      </p>
      <ul className="space-y-1 text-xs text-slate-700" data-testid="auto-capture-list">
        <Row icon={isCapturingScreenshot ? Loader2 : screenshotError ? ImageIcon : Check} spin={isCapturingScreenshot}>
          {isCapturingScreenshot
            ? 'Capturing screenshot…'
            : screenshotError
            ? 'Screenshot unavailable'
            : 'Screenshot captured'}
        </Row>
        {payload && (
          <>
            <Row icon={Check}>
              App: <strong>{payload.app}</strong> · Route: <span className="font-mono">{payload.route}</span>
            </Row>
            {payload.user?.email && (
              <Row icon={Check}>
                User: <span className="font-mono">{payload.user.email}</span>
              </Row>
            )}
            <Row icon={Check}>
              Device: {payload.device} · {payload.os}
            </Row>
            <Row icon={Check}>
              Browser: {payload.browser} · Viewport: {payload.viewport}
            </Row>
            {payload.recentActions && payload.recentActions.length > 0 && (
              <Row icon={Check}>
                {payload.recentActions.length} recent actions logged
              </Row>
            )}
          </>
        )}
      </ul>
    </div>
  )
}

function Row({
  icon: Icon,
  children,
  spin,
}: {
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  spin?: boolean
}) {
  return (
    <li className="flex items-center gap-1.5">
      <Icon className={cn('h-3 w-3 text-emerald-600', spin && 'animate-spin text-slate-500')} />
      <span>{children}</span>
    </li>
  )
}
